import React, { Component } from 'react';
import {
  func, shape, bool
} from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { withLang, langType } from '../../../i18n';
import { publicationStatusValues, surveyPartTypeValues, SurveySchemaPartEditorType } from '../../../constants/types';
import styles from '../preview.module.css';
import editorStyles from '../../Editor/editor.module.css';
import {
  clearSurveySchemaPartOptionsAction,
  createNewSchemaPartVersionAction, getOrCheckSurveySchemaPartCodeAction,
  postSurveyPartAction,
  putSurveySchemaPartAction,
  updateSurveySchemaPartOptionsAction
} from '../../../actions';
import { routes } from '../../../constants';
import ConfirmButton from '../../ConfirmButton';
import TabsContainer from '../../TabsContainer';
import TabPane from '../../TabPane';
import SchemaPartQuestionsPreview from './QuestionsPreview';
import SchemaPartFeedbackPreview from './FeedbackPreview';
import {
  getCompiledPath, getSuffixedRoute, getSurveyPartType, isSurveySchemaPartType, setDocumentTitle
} from '../../../util';
import { getSurveySchemaPartUsage, getExtraSurveyPartUsage } from '../../../api';
import ConnectedSurveySchemas from './ConnectedSurveySchemas';
import PreviewLanguageSelector from '../PreviewLanguageSelector';
import withRouter from '../../WithRouter';

const compileReturnRoute = (route, surveyPartId = {}) => getCompiledPath(route, surveyPartId);

class SchemaPartPreview extends Component {
  constructor(props) {
    super(props);

    this.onLanguageSelect = this.onLanguageSelect.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onSave = this.onSave.bind(this);
    this.onSchemaPartStateChange = this.onSchemaPartStateChange.bind(this);
    this.onToggleSchemaSelection = this.onToggleSchemaSelection.bind(this);

    const { surveySchemaPart, location, lang } = props;

    const affectsConnectedSurveySchemas = location.state?.isCreateNewVersion
      || (publicationStatusValues.DRAFT === surveySchemaPart.status
        && surveySchemaPart.version > 1);

    this.state = {
      createNew: !surveySchemaPart.id,
      previewLang: lang,
      isCreateNewVersion: location.state?.isCreateNewVersion,
      isCreateNewCopy: location.state?.isCreateNewCopy,
      affectsConnectedSurveySchemas,
      usage: [],
      selectedSurveySchemas: [],
      submitError: false
    };
  }

  componentDidMount() {
    if (this.state.affectsConnectedSurveySchemas) {
      const surveyPartId = this.state.isCreateNewVersion
        ? this.props.surveySchemaPart.id : this.props.surveySchemaPart.previousVersionId;
      const surveyPartType = getSurveyPartType(this.props.location.pathname);
      const getSurveyPartUsage = surveyPartType === surveyPartTypeValues.SURVEY_SCHEMA_PART
        ? getSurveySchemaPartUsage : getExtraSurveyPartUsage;

      getSurveyPartUsage(surveyPartId)
        .then((usage) => this.setState({ usage }));
    }
  }

  onLanguageSelect(previewLang) {
    this.setState({ previewLang });
  }

  onCancel() {
    this.clearAndReturn(this.props.surveySchemaPart.id);
  }

  async onSave() {
    const {
      surveySchemaPart, dispatchPutSurveyPart,
      dispatchPostSurveyPart, dispatchPostCreateNewVersion,
      dispatchCheckCode
    } = this.props;

    const {
      createNew, isCreateNewCopy, isCreateNewVersion,
      affectsConnectedSurveySchemas, selectedSurveySchemas
    } = this.state;

    try {
      if (createNew || isCreateNewCopy) {
        const codeResult = await dispatchCheckCode(surveySchemaPart.code);
        if (!(codeResult.value.available && codeResult.value.valid)) {
          return;
        }
      }

      let action;
      if (isCreateNewVersion) {
        const surveySchemaIds = this.state.selectedSurveySchemas;
        action = dispatchPostCreateNewVersion(surveySchemaPart.id,
          { surveySchemaPart, surveySchemaIds });
      } else if (isCreateNewCopy) {
        action = dispatchPostSurveyPart(surveySchemaPart);
      } else if (surveySchemaPart.isExistingSchemaPart) {
        let surveySchemaIds = [];
        if (affectsConnectedSurveySchemas
          && publicationStatusValues.PUBLISHED === surveySchemaPart.status) {
          surveySchemaIds = selectedSurveySchemas;
        }
        action = dispatchPutSurveyPart(surveySchemaPart.id,
          { surveySchemaPart, surveySchemaIds });
      } else {
        action = dispatchPostSurveyPart(surveySchemaPart);
      }

      const saveResult = await action;
      this.clearAndReturn(saveResult.value.id);
    } catch (e) {
      this.setState({ submitError: true });
    }
  }

  onSchemaPartStateChange(event) {
    this.props.dispatchUpdate({ status: event.target.value });
  }

  onToggleSchemaSelection(event) {
    const selected = this.state.selectedSurveySchemas;
    const schemaId = Number(event.target.value);
    if (selected.includes(schemaId)) {
      this.setState({ selectedSurveySchemas: selected.filter((s) => s !== schemaId) });
    } else {
      this.setState({ selectedSurveySchemas: selected.concat(schemaId) });
    }
  }

  clearAndReturn(surveyPartId) {
    let returnRoute;
    if (this.props.surveySchemaPart.type === surveyPartTypeValues.SURVEY_SCHEMA_PART) {
      returnRoute = surveyPartId
        ? compileReturnRoute(routes.surveySchemas.surveyParts.surveyPart.index, { surveyPartId })
        : routes.surveySchemas.surveyParts.index;
    } else {
      // view extra survey part not yet implemented
      returnRoute = `${routes.surveySchemas.extraSurveyParts.index}`;
    }

    this.props.dispatchClear();
    this.props.navigate(returnRoute);
  }

  renderSurveyPartComponents() {
    const { surveySchemaPart, translate: t } = this.props;
    const { previewLang } = this.state;

    if (isSurveySchemaPartType(surveySchemaPart.type)) {
      return (
        <TabsContainer>
          <TabPane key="questions" label={t('surveyParts.questions')}>
            <SchemaPartQuestionsPreview
              schemaPart={surveySchemaPart}
              lang={previewLang}
              translate={t}
            />
          </TabPane>
          <TabPane key="feedback" label={t('surveySchemaParts.dimensions.dimensions')}>
            <SchemaPartFeedbackPreview
              schemaPart={surveySchemaPart}
              previewLang={previewLang}
            />
          </TabPane>
        </TabsContainer>
      );
    }

    return (
      <SchemaPartQuestionsPreview
        schemaPart={surveySchemaPart}
        lang={previewLang}
        translate={t}
      />
    );
  }

  render() {
    const { translate: t, surveySchemaPart, lang } = this.props;
    const surveyPartId = surveySchemaPart.id;
    const { previewLang, codeReserved, submitError } = this.state;
    const variableQuestionsSelected = surveySchemaPart.variables
      .every((v) => v.questions && v.questions.length);
    const translationBase = isSurveySchemaPartType(surveySchemaPart.type)
      ? 'surveySchemaParts'
      : 'extraSurveyParts';

    const documentTitleTranslationKey = isSurveySchemaPartType(surveySchemaPart.type)
      ? 'surveySchemaParts.surveyPart'
      : 'extraSurveyParts.title';

    const submitDisabled = codeReserved || submitError || !variableQuestionsSelected;

    const returnToEditorRoute = () => {
      const routeRoot = surveySchemaPart.type === surveyPartTypeValues.SURVEY_SCHEMA_PART
        ? routes.surveySchemas.surveyParts
        : routes.surveySchemas.extraSurveyParts;

      if (this.state.isCreateNewCopy) {
        return compileReturnRoute(routeRoot.surveyPart.copy.index, { surveyPartId });
      } if (this.state.createNew) {
        const route = this.props.location.pathname;
        return getSuffixedRoute(route, routeRoot.create.index);
      } if (this.state.isCreateNewVersion) {
        return compileReturnRoute(routeRoot.surveyPart.newVersion.index, { surveyPartId });
      }
      return compileReturnRoute(routeRoot.surveyPart.edit.index, { surveyPartId });
    };

    setDocumentTitle(`${t(documentTitleTranslationKey)} - ${t('common.preview')}`);

    return (
      <div className="page-container">
        <h1 className={styles.previewHeader}>{t('common.preview')}</h1>
        <PreviewLanguageSelector
          previewLang={previewLang}
          onLanguageSelect={this.onLanguageSelect}
        />
        {this.renderSurveyPartComponents()}

        <div className={styles.stateContainer}>
          <p>
            {this.state.createNew
              ? t(`${translationBase}.preview.newSurveyPartStateExplanation`)
              : t(`${translationBase}.preview.existingSurveyPartStateExplanation`)}
          </p>
          <div className={styles.stateInputs}>
            {[publicationStatusValues.DRAFT, publicationStatusValues.PUBLISHED].map((status) => (
              <div key={status} className={styles.stateInput}>
                <input
                  type="radio"
                  name="status"
                  id={`publicationStatus${status}`}
                  value={status}
                  checked={surveySchemaPart.status === status}
                  onChange={this.onSchemaPartStateChange}
                  disabled={surveySchemaPart.lockedFromEditing}
                />
                <label htmlFor={`publicationStatus${status}`}>{t(`status.${status}`)}</label>
              </div>
            ))}
          </div>
          {(this.state.affectsConnectedSurveySchemas
            && publicationStatusValues.PUBLISHED === surveySchemaPart.status
            && this.state.usage.surveySchemas)
            && (
            <ConnectedSurveySchemas
              surveySchemaLinks={this.state.usage.surveySchemas}
              selectedSurveySchemas={this.state.selectedSurveySchemas}
              onSelect={this.onToggleSchemaSelection}
              translate={t}
              currentLanguage={lang}
            />
            )}
        </div>

        {codeReserved && (
        <div className={`${editorStyles.errorContainer} ${editorStyles.error}`}>
          <p>{t('editor.identifierError')}</p>
        </div>
        )}

        {submitError && (
        <div className={`${editorStyles.errorContainer} ${editorStyles.error}`}>
          <p>{t('common.saveError')}</p>
        </div>
        )}

        {!variableQuestionsSelected && (
        <div className={`${editorStyles.errorContainer} ${editorStyles.error}`}>
          <p>{t('editor.surveySchemaPartPreview.variableQuestionsNotSelected')}</p>
        </div>
        )}

        <div className={editorStyles.navigation}>
          <ConfirmButton onConfirm={this.onCancel} modalText={t('confirmButton.confirmCancel')}>
            {t('common.cancel')}
          </ConfirmButton>
          <Link className="button--action" to={returnToEditorRoute()}>
            {t('common.back')}
          </Link>
          <button
            type="button"
            className={`button button--action${submitDisabled ? ' disabled' : ''}`}
            onClick={this.onSave}
            disabled={submitDisabled}
          >
            {t('common.save')}
          </button>
        </div>
      </div>
    );
  }
}

SchemaPartPreview.propTypes = {
  location: shape({
    state: shape({
      isCreateNewVersion: bool.isRequired,
      isCreateNewCopy: bool.isRequired
    }).isRequired
  }).isRequired,
  surveySchemaPart: SurveySchemaPartEditorType.isRequired,
  dispatchUpdate: func.isRequired,
  dispatchClear: func.isRequired,
  dispatchPostSurveyPart: func.isRequired,
  dispatchPutSurveyPart: func.isRequired,
  dispatchPostCreateNewVersion: func.isRequired,
  dispatchCheckCode: func.isRequired,
  navigate: func.isRequired,
  ...langType
};

const mapStateToProps = ({ surveySchemaPart }) => ({
  surveySchemaPart: surveySchemaPart.surveySchemaPartOptions
});

const mapDispatchToProps = (dispatch) => ({
  dispatchUpdate: (options) =>
    dispatch(updateSurveySchemaPartOptionsAction(options)),
  dispatchClear: () =>
    dispatch(clearSurveySchemaPartOptionsAction()),
  dispatchPostSurveyPart: (options) =>
    dispatch(postSurveyPartAction(options)),
  dispatchPutSurveyPart: (schemaPartId, options) =>
    dispatch(putSurveySchemaPartAction(schemaPartId, options)),
  dispatchPostCreateNewVersion: (schemaPartId, options) =>
    dispatch(createNewSchemaPartVersionAction(schemaPartId, options)),
  dispatchCheckCode: (suggestion) =>
    dispatch(getOrCheckSurveySchemaPartCodeAction(suggestion))
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withLang(SchemaPartPreview)));
